import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usersApi, activityApi, screenshotsApi, aiApi } from '../services/api';

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id!);
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [topApps, setTopApps] = useState<any[]>([]);
  const [screenshots, setScreenshots] = useState<any[]>([]);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [userId, date]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, t, s, apps, shots, ai] = await Promise.all([
        usersApi.findOne ? usersApi.findOne(userId) : Promise.resolve(null),
        activityApi.getTimeline(userId, date),
        activityApi.getDayStats(userId, date),
        activityApi.getTopApps(userId, date),
        screenshotsApi.getUser(userId, date),
        aiApi.getDailySummary(userId, date),
      ]);
      setUser(u);
      setTimeline(t);
      setStats(s);
      setTopApps(apps);
      setScreenshots(shots);
      setAiSummary(ai?.summary || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const productivePct = stats?.productiveSecs && stats?.totalHeartbeats
    ? Math.round((stats.productiveSecs / (stats.totalHeartbeats * 30)) * 100)
    : 0;

  return (
    <div style={{ padding: '28px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Link to="/employees" style={{ color: 'var(--text3)', fontSize: 13 }}>← Empleados</Link>
        <span style={{ color: 'var(--text3)' }}>/</span>
        <span style={{ fontSize: 13 }}>{user?.name || `Empleado #${userId}`}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '6px 12px' }}
            onClick={() => navigate(`/remote/${userId}`)}
            title="Escritorio remoto"
          >
            🖥️ Escritorio remoto
          </button>
          <input
            type="date"
            className="input"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{ width: 160 }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Productividad', value: `${productivePct}%`, color: productivePct >= 70 ? 'var(--green)' : productivePct >= 40 ? 'var(--accent)' : 'var(--red)' },
          { label: 'Tiempo productivo', value: formatTime(stats?.productiveSecs || 0), color: 'var(--green)' },
          { label: 'Tiempo improductivo', value: formatTime(stats?.unproductiveSecs || 0), color: 'var(--red)' },
          { label: 'Tiempo inactivo', value: formatTime(stats?.idleSecs || 0), color: 'var(--text3)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* AI Summary */}
        {aiSummary && (
          <div className="card" style={{ borderColor: 'rgba(239,162,1,0.2)', background: 'rgba(239,162,1,0.04)' }}>
            <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              ✨ Resumen IA
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{aiSummary}</p>
          </div>
        )}

        {/* Top Apps */}
        <div className="card">
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>💻 Aplicaciones más usadas</h3>
          {topApps.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 12 }}>Sin datos</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topApps.slice(0, 8).map((app: any, i) => {
                const maxSecs = topApps[0]?.totalSecs || 1;
                const pct = Math.round((app.totalSecs / maxSecs) * 100);
                const color = app.productivity === 'productive' ? 'var(--green)' : app.productivity === 'unproductive' ? 'var(--red)' : 'var(--blue)';
                const h = Math.floor(app.totalSecs / 3600);
                const m = Math.floor((app.totalSecs % 3600) / 60);
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12 }}>{app.appName}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{h > 0 ? `${h}h ` : ''}{m}m</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>📅 Línea temporal del día</h3>
          {timeline.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
              Sin actividad registrada para este día
            </div>
          ) : (
            <div style={{ position: 'relative', height: 40, background: 'var(--bg3)', borderRadius: 8, overflow: 'hidden' }}>
              {timeline.map((beat: any, i) => {
                const startOfDay = new Date(date + 'T08:00:00').getTime();
                const endOfDay = new Date(date + 'T20:00:00').getTime();
                const totalMs = endOfDay - startOfDay;
                const beatTime = new Date(beat.timestamp).getTime();
                const left = Math.max(0, Math.min(100, ((beatTime - startOfDay) / totalMs) * 100));
                const color = beat.productivity === 'productive' ? '#10b981' :
                  beat.productivity === 'unproductive' ? '#ef4444' :
                  beat.productivity === 'idle' ? '#374151' : '#3b82f6';
                return (
                  <div
                    key={i}
                    title={`${new Date(beat.timestamp).toLocaleTimeString('es-ES')} — ${beat.appName || 'Sin app'} (${beat.productivity})`}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      top: 0,
                      width: '0.5%',
                      minWidth: 3,
                      height: '100%',
                      background: color,
                      opacity: 0.8,
                    }}
                  />
                );
              })}
              {/* Time labels */}
              {['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map((t, i) => (
                <div key={t} style={{
                  position: 'absolute',
                  bottom: -18,
                  left: `${(i / 6) * 100}%`,
                  fontSize: 9,
                  color: 'var(--text3)',
                  transform: 'translateX(-50%)',
                }}>{t}</div>
              ))}
            </div>
          )}
          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 28, fontSize: 11, color: 'var(--text3)' }}>
            {[
              { color: '#10b981', label: 'Productivo' },
              { color: '#3b82f6', label: 'Neutral' },
              { color: '#ef4444', label: 'Improductivo' },
              { color: '#374151', label: 'Inactivo' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                {l.label}
              </div>
            ))}
          </div>
        </div>

        {/* Screenshots */}
        {screenshots.length > 0 && (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>🖼️ Capturas del día</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {screenshots.slice(0, 12).map((s: any) => (
                <div key={s.id} style={{
                  background: 'var(--bg3)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid var(--border)',
                }}>
                  <div style={{
                    height: 90,
                    background: 'var(--bg4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                  }}>
                    🖼️
                  </div>
                  <div style={{ padding: '6px 8px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                      {new Date(s.takenAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.appName || '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
